import { useParams, Link, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  const handleTabClick = (catSlug: string) => {
    navigate(`/category/${catSlug}`);
  };

  if (!category) {
    const firstCategory = categories[0];
    return (
      <>
        <SEOHead title="分类页" description="浏览国产久久久内容分类" />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
              <i className="ri-error-warning-line text-2xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 font-heading mb-2">分类不存在</h2>
            <p className="text-sm text-foreground-500 mb-6">请从以下分类中选择浏览</p>
            <Link
              to={`/category/${firstCategory.slug}`}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              前往{firstCategory.name}
              <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - ${siteInfo.name}`,
    description: category.description,
    url: `${siteInfo.url}/category/${category.slug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: category.name, item: `${siteInfo.url}/category/${category.slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 国产久久久`}
        description={category.description}
        keywords={`国产久久久,${category.name},国产久久久${category.name},国产内容,精选内容`}
        canonicalUrl={`/category/${category.slug}`}
        schema={[categorySchema, breadcrumbSchema]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="mt-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${category.icon} text-xl text-primary-600`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">{category.name}</h1>
              </div>
              <p className="text-sm text-foreground-500">{category.description}</p>
            </div>
          </div>
        </div>

        <div className="border-b border-background-200/60 bg-background-50 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabClick(cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200/50 transition-all duration-300 cursor-pointer flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-base font-semibold text-foreground-900 font-heading mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2 flex-1">{article.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <div className="flex items-center gap-3">
                        <span>{article.date}</span>
                        <span>{article.views.toLocaleString()} 阅读</span>
                      </div>
                      <span>{article.readTime} 分钟</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-inbox-line text-2xl text-secondary-600"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}